import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Slider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CropIcon from '@mui/icons-material/Crop';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import EventsApi from '../../../api/eventsApi/eventsApi';
import Cropper, { Area } from 'react-easy-crop';

interface CoverPhotoModalProps {
  open: boolean;
  onClose: () => void;
  pinId: string;
  userId: string;
  onPhotoUploaded?: () => void;
}

const CoverPhotoModal: React.FC<CoverPhotoModalProps> = ({
  open,
  onClose,
  pinId,
  userId,
  onPhotoUploaded
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [existingCoverPhoto, setExistingCoverPhoto] = useState<string | null>(null);
  const [existingCoverPhotoId, setExistingCoverPhotoId] = useState<string | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showCropEditor, setShowCropEditor] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const CANVAS_WIDTH = 1200; // Standart genişlik
  const CANVAS_HEIGHT = 630; // 16:9 oranı için uygun yükseklik
  const ASPECT_RATIO = CANVAS_WIDTH / CANVAS_HEIGHT; // ~1.905

  useEffect(() => {
    const fetchCoverPhoto = async () => {
      if (pinId) {
        try {
          const pinInfo = await EventsApi.getPinFullReport(pinId);
          const coverPhoto = pinInfo.pinInfo.fileAttachments?.find(file => 
            file.fileName.startsWith('cover') && 
            (file.fileName.endsWith('.jpg') || file.fileName.endsWith('.jpeg') || file.fileName.endsWith('.png'))
          );

          if (coverPhoto) {
            const photoBlob = await EventsApi.getFileAttachment(coverPhoto.id);
            const photoUrl = URL.createObjectURL(photoBlob);
            setExistingCoverPhoto(photoUrl);
            setExistingCoverPhotoId(coverPhoto.id);
            setPreviewUrl(photoUrl);
            setImageSrc(photoUrl);
          } else {
            setExistingCoverPhoto(null);
            setExistingCoverPhotoId(null);
            setPreviewUrl(null);
            setImageSrc(null);
          }
        } catch (error) {
          console.error('Error fetching cover photo:', error);
        }
      }
    };

    if (open) {
      fetchCoverPhoto();
    }

    return () => {
      // Cleanup
      if (existingCoverPhoto) {
        URL.revokeObjectURL(existingCoverPhoto);
      }
      if (imageSrc && imageSrc !== existingCoverPhoto) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [open, pinId]);

  const createImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });
  };

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    // Canvas boyutlarını hedef boyutlara ayarla
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;

    // Arka planı beyaz yap
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Crop edilmiş resmi çiz
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      CANVAS_WIDTH,
      CANVAS_HEIGHT
    );

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas to Blob conversion failed'));
          }
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          setImageSrc(result);
          setPreviewUrl(result);
          setShowCropEditor(true);
          setCrop({ x: 0, y: 0 });
          setZoom(1);
        };
        reader.readAsDataURL(file);
        setSelectedFile(file);
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
  };

  const handleSaveCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], selectedFile?.name || 'cover.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      
      // Önizleme için blob URL oluştur
      const previewBlobUrl = URL.createObjectURL(croppedBlob);
      setPreviewUrl(previewBlobUrl);
      setSelectedFile(croppedFile);
      setShowCropEditor(false);
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !pinId || !userId) return;

    try {
      setIsUploading(true);
      const fileExtension = selectedFile.name.split('.').pop() || 'jpg';
      const newFileName = `cover${pinId}.${fileExtension}`;
      
      const renamedFile = new File([selectedFile], newFileName, {
        type: selectedFile.type,
        lastModified: selectedFile.lastModified,
      });

      const formData = new FormData();
      formData.append('Files', renamedFile);
      formData.append('Id', pinId);
      formData.append('UserId', userId);

      await EventsApi.updatePin(pinId, formData);
      onPhotoUploaded?.();
      onClose();
    } catch (error) {
      console.error('Error uploading cover photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = async () => {
    // Eğer mevcut bir kapak fotoğrafı varsa, backend'den sil
    if (existingCoverPhotoId) {
      try {
        setIsDeleting(true);
        await EventsApi.deleteFileAttachment(existingCoverPhotoId);
        setExistingCoverPhotoId(null);
        setExistingCoverPhoto(null);
        setPreviewUrl(null);
        setImageSrc(null);
        setSelectedFile(null);
        setShowCropEditor(false);
        onPhotoUploaded?.(); // Callback'i çağır ki liste güncellensin
      } catch (error) {
        console.error('Error deleting cover photo:', error);
        alert('Kapak fotoğrafı silinirken bir hata oluştu.');
      } finally {
        setIsDeleting(false);
      }
    } else {
      // Sadece seçili dosya varsa, sadece state'i temizle
      setSelectedFile(null);
      setPreviewUrl(null);
      setImageSrc(null);
      setShowCropEditor(false);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditPhoto = () => {
    if (previewUrl || existingCoverPhoto) {
      setImageSrc(previewUrl || existingCoverPhoto);
      setShowCropEditor(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Kapak Fotoğrafı Ekle ve Düzenle</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          {showCropEditor && imageSrc ? (
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 400,
                  bgcolor: '#000',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 2
                }}
              >
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={ASPECT_RATIO}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="rect"
                  showGrid={true}
                />
              </Box>
              
              <Box sx={{ px: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <ZoomOutIcon sx={{ color: '#094151' }} />
                  <Slider
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(e, value) => setZoom(value as number)}
                    sx={{
                      flex: 1,
                      color: '#094151',
                      '& .MuiSlider-thumb': {
                        backgroundColor: '#094151',
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: '#094151',
                      },
                    }}
                  />
                  <ZoomInIcon sx={{ color: '#094151' }} />
                </Box>
                <Typography variant="body2" color="textSecondary" align="center">
                  Zoom: {zoom.toFixed(1)}x - Fotoğrafı sürükleyerek konumlandırabilirsiniz
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setShowCropEditor(false);
                    if (!selectedFile) {
                      setImageSrc(null);
                      setPreviewUrl(null);
                    }
                  }}
                  sx={{
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#b71c1c',
                      color: '#fff',
                    }
                  }}
                >
                  İptal
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveCrop}
                  startIcon={<CropIcon />}
                  sx={{
                    backgroundColor: '#094151',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#022631',
                    }
                  }}
                >
                  Kırp ve Kaydet
                </Button>
              </Box>
            </Box>
          ) : (previewUrl || existingCoverPhoto) ? (
            <Box
              sx={{
                width: '100%',
                position: 'relative',
                '&::before': {
                  content: '""',
                  display: 'block',
                  paddingTop: '52.5%', // 630/1200 = 0.525 (16:9 oranı)
                },
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <img
                src={previewUrl || existingCoverPhoto || ''}
                alt="Preview"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
              <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1 }}>
                <IconButton
                  onClick={handleEditPhoto}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                    }
                  }}
                  title="Düzenle"
                >
                  <CropIcon sx={{ color: '#094151' }} />
                </IconButton>
                <IconButton
                  onClick={handleRemoveFile}
                  disabled={isDeleting}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 1)',
                    },
                    '&:disabled': {
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                    }
                  }}
                  title="Sil"
                >
                  <DeleteIcon sx={{ color: '#d32f2f' }} />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box
              onClick={handleClickUpload}
              sx={{
                width: '100%',
                position: 'relative',
                '&::before': {
                  content: '""',
                  display: 'block',
                  paddingTop: '52.5%',
                },
                border: '2px dashed #b3c2d1',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                bgcolor: '#f3f6fa',
                '&:hover': {
                  bgcolor: '#e8eef5'
                }
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: '#094151', mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  Fotoğraf seçmek için tıklayın
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#d32f2f',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#b71c1c',
              color: '#fff',
            }
          }}
        >
          İptal
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading || showCropEditor}
          sx={{
            backgroundColor: '#094151',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#022631',
              color: '#fff',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#fff',
            }
          }}
        >
          {isUploading ? 'Yükleniyor...' : 'Yükle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverPhotoModal;
